import React, { useState, useEffect, useCallback, useRef } from "react";
import { Paper, Typography, Box, IconButton, Fade, alpha } from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import useApp from "../../store/contexts/AppContext";

// Keyframe animations
const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Styled components
const AdContainer = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  overflow: "hidden",
  backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
  border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(212,68,1,0.1)"}`,
  boxShadow: theme.palette.mode === "dark"
    ? "0 4px 20px rgba(0, 0, 0, 0.3)"
    : "0 4px 20px rgba(212, 68, 1, 0.08)",
  minHeight: 200,
  marginTop: 10,
  animation: `${slideIn} 0.5s ease-out`,
  transition: "all 0.3s ease",
  "&:hover": {
    boxShadow: theme.palette.mode === "dark"
      ? "0 6px 24px rgba(212, 68, 1, 0.3)"
      : "0 6px 24px rgba(212, 68, 1, 0.2)",
    border: `1px solid rgba(212, 68, 1, 0.4)`,
  },
}));

const AdHeader = styled(Box)(({ theme }) => ({
  padding: "16px 20px",
  borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
  backgroundColor: theme.palette.mode === "dark" ? "#222" : "#f8f9fa",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  transition: "background-color 0.3s ease",
}));

const HeaderTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#fff" : "#111827",
  fontWeight: 600,
  fontSize: "1.1rem",
  fontFamily:
    "apple-system, system-ui, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  transition: "color 0.3s ease",
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  color: alpha("#fff", 0.6),
  width: 28,
  height: 28,
  transition: "all 0.3s ease",
  "&:hover": {
    color: "#fff",
    backgroundColor: alpha("#fff", 0.1),
    transform: "rotate(90deg)",
  },
}));

const AdContent = styled(Box)(({ theme, hasAd }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: hasAd ? "stretch" : "center",
  justifyContent: hasAd ? "flex-start" : "center",
  padding: hasAd ? 0 : "30px 20px",
  color: theme.palette.mode === "dark" ? "#999" : "#6b7280",
  textAlign: "center",
  backgroundColor: theme.palette.mode === "dark" ? "#1a1a1f" : "#ffffff",
  minHeight: 180,
  transition: "background-color 0.3s ease",
}));

const AdImage = styled("img")(({ theme }) => ({
  width: "100%",
  height: 200,
  objectFit: "cover",
  cursor: "pointer",
  transition: "transform 0.3s ease",
  "&:hover": {
    transform: "scale(1.05)",
  },
}));

const AdDetails = styled(Box)(({ theme }) => ({
  padding: "16px 20px",
}));

const AdTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  fontSize: "1rem",
  marginBottom: 8,
  color: theme.palette.mode === "dark" ? "#fff" : "#111827",
  lineHeight: 1.3,
  transition: "color 0.3s ease",
}));

const AdDescription = styled(Typography)(({ theme }) => ({
  fontSize: "0.85rem",
  color: theme.palette.mode === "dark" ? alpha("#fff", 0.6) : alpha("#111827", 0.6),
  marginBottom: 16,
  lineHeight: 1.5,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  transition: "color 0.3s ease",
}));

const CTAButton = styled(Box)(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  padding: theme.spacing(1, 2),
  borderRadius: 8,
  backgroundColor: alpha("#d44401", 0.15),
  color: "#ff6b35",
  fontSize: "0.85rem",
  fontWeight: 600,
  cursor: "pointer",
  transition: "all 0.3s ease",
  border: "1px solid rgba(212, 68, 1, 0.3)",
  "&:hover": {
    backgroundColor: alpha("#d44401", 0.25),
    transform: "translateX(3px)",
    borderColor: "rgba(212, 68, 1, 0.5)",
  },
}));

const EmptyState = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
}));

const AdText = styled(Typography)(({ theme }) => ({
  color: "#888",
  textAlign: "center",
  fontSize: "0.9rem",
}));

const LoadingContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: 200,
  overflow: "hidden",
  backgroundColor: alpha("#fff", 0.03),
  animation: `${shimmer} 2s ease-in-out infinite`,
  background: `linear-gradient(
    90deg,
    ${alpha("#fff", 0.03)} 0%,
    ${alpha("#fff", 0.08)} 50%,
    ${alpha("#fff", 0.03)} 100%
  )`,
  backgroundSize: "1000px 100%",
}));

const Advertisement = () => {
  const {
    appState: { userData },
    getActiveAdsApiCall,
    recordAdViewApiCall,
    recordAdClickApiCall,
  } = useApp();

  const [ad, setAd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [viewRecorded, setViewRecorded] = useState(false);
  const hasFetchedRef = useRef(false);

  // Fetch a single ad for sidebar
  const fetchAd = useCallback(async () => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    try {
      setLoading(true);
      const result = await getActiveAdsApiCall();

      if (result.success && result.data) {
        const adsData = Array.isArray(result.data)
          ? result.data
          : result.data.ads || [];

        const userAccountType = (
          userData?.account_type || "basic"
        ).toLowerCase();

        // Filter ads by account type
        const filteredAds = adsData.filter((ad) => {
          const visiblePlans = ad.visible_to_plans || [
            "basic",
            "standard",
            "premium",
            "elite",
          ];
          return visiblePlans.includes(userAccountType);
        });

        // Pick a random ad
        if (filteredAds.length > 0) {
          const randomIndex = Math.floor(Math.random() * filteredAds.length);
          setAd(filteredAds[randomIndex]);
        }
      }
    } catch (error) {
      console.error("Error fetching ad:", error);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Record ad view after 1 second
  useEffect(() => {
    if (ad && !dismissed && !viewRecorded) {
      const timer = setTimeout(async () => {
        try {
          await recordAdViewApiCall(ad.id);
          setViewRecorded(true);
        } catch (error) {
          console.error("Error recording view:", error);
        }
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [ad, dismissed, viewRecorded, recordAdViewApiCall]);

  // Initial fetch
  useEffect(() => {
    if (userData?.id) {
      fetchAd();
    }
  }, [userData?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle ad click
  const handleAdClick = async () => {
    if (!ad) return;

    try {
      await recordAdClickApiCall(ad.id);
      if (ad.click_url) {
        window.open(ad.click_url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error recording click:", error);
    }
  };

  if (loading) {
    return (
      <AdContainer elevation={1}>
        <AdHeader>
          <HeaderTitle>Advertisements</HeaderTitle>
        </AdHeader>
        <LoadingContainer />
      </AdContainer>
    );
  }

  if (dismissed || !ad) {
    return (
      <AdContainer elevation={1}>
        <AdHeader>
          <HeaderTitle>Advertisements</HeaderTitle>
        </AdHeader>
        <AdContent hasAd={false}>
          <EmptyState>
            <AdsClickIcon sx={{ fontSize: 40, color: "#444", mb: 2 }} />
            <AdText>Advertisement content will appear here</AdText>
          </EmptyState>
        </AdContent>
      </AdContainer>
    );
  }

  return (
    <Fade in={!dismissed} timeout={500}>
      <AdContainer elevation={1}>
        <AdHeader>
          <HeaderTitle>Advertisements</HeaderTitle>
          <CloseButton size="small" onClick={() => setDismissed(true)}>
            <CloseIcon fontSize="small" />
          </CloseButton>
        </AdHeader>

        <AdContent hasAd={true}>
          {ad.media?.[0] && (
            <AdImage
              src={ad.media[0].file_path}
              alt={ad.title}
              onClick={handleAdClick}
              loading="lazy"
            />
          )}

          <AdDetails>
            <AdTitle>{ad.title}</AdTitle>
            {ad.description && <AdDescription>{ad.description}</AdDescription>}
            {ad.click_url && (
              <CTAButton onClick={handleAdClick}>
                <span>Learn More</span>
                <OpenInNewIcon sx={{ fontSize: "0.9rem" }} />
              </CTAButton>
            )}
          </AdDetails>
        </AdContent>
      </AdContainer>
    </Fade>
  );
};

export default Advertisement;
